import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { MapTheme } from '@shared/types';
import { config } from '@shared/config';
import { clientSettings } from '../ClientSettings';

export class GameScene {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;

  private ambientLight: THREE.HemisphereLight | null = null;
  private sunLight: THREE.DirectionalLight | null = null;
  private container: HTMLElement;

  // Post-processing
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private fxaaPass: ShaderPass | null = null;

  // Environment map (IBL)
  private envTexture: THREE.Texture | null = null;

  /** True if we detected a low-power device and reduced quality. */
  lowQuality = false;

  constructor(container: HTMLElement) {
    this.container = container;

    // Detect low-power devices: mobile/tablet or small screens
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      || ('ontouchstart' in window && window.innerWidth < 1024);

    this.lowQuality = isMobile;

    this.renderer = new THREE.WebGLRenderer({
      antialias: !isMobile, // disable AA on mobile (FXAA covers desktop instead)
      powerPreference: 'high-performance',
    });

    // Cap pixel ratio to 2x — beyond that is diminishing returns for heavy cost
    this.renderer.setPixelRatio(clientSettings.getPixelRatio(isMobile));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.shadowMap.enabled = clientSettings.isShadowEnabled();
    // PCFSoftShadowMap on desktop for softer, higher-quality shadow edges
    this.renderer.shadowMap.type = isMobile ? THREE.BasicShadowMap : THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();

    // ── Environment map for metallic/glossy surface reflections ──
    // RoomEnvironment generates a neutral studio-lighting PMREM cubemap.
    // We keep intensity low so it adds subtle reflective sheen without
    // fighting the per-scene directional lighting.
    if (!isMobile) {
      const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
      pmremGenerator.compileEquirectangularShader();
      const roomEnv = new RoomEnvironment();
      this.envTexture = pmremGenerator.fromScene(roomEnv).texture;
      this.scene.environment = this.envTexture;
      // environmentIntensity is Three.js r163+ — controls IBL contribution
      this.scene.environmentIntensity = 0.35;
      pmremGenerator.dispose();
      roomEnv.dispose();
    }

    // ── Post-processing composer (desktop only) ──
    // On mobile we fall back to a direct renderer.render() call.
    if (!isMobile) {
      this.setupComposer(container.clientWidth, container.clientHeight);
    }

    window.addEventListener('resize', this.resize.bind(this));

    // Listen for settings changes and apply them live
    clientSettings.onChange(() => this.applyClientSettings());
  }

  // ────────────────────────────────────────────────────────────
  // Composer setup
  // ────────────────────────────────────────────────────────────

  private setupComposer(width: number, height: number): void {
    this.composer = new EffectComposer(this.renderer);

    // RenderPass: renders the scene into the composer's internal buffer.
    // The camera is a placeholder here — it is replaced every frame in render()
    // before the composer runs, so Game.ts's FollowCamera is always used.
    const placeholderCamera = new THREE.PerspectiveCamera();
    this.renderPass = new RenderPass(this.scene, placeholderCamera);
    this.composer.addPass(this.renderPass);

    // UnrealBloomPass — high threshold so only genuinely bright emissives
    // (neon road stripes, boost sparks, coin glints) actually bloom.
    // Normal surfaces stay clean.
    const size = new THREE.Vector2(width, height);
    this.bloomPass = new UnrealBloomPass(size, 0.3, 0.4, 0.85);
    this.composer.addPass(this.bloomPass);

    // FXAA — smooth edges without the GPU cost of hardware MSAA.
    // Must know the exact pixel size, so it's updated on every resize.
    this.fxaaPass = new ShaderPass(FXAAShader);
    this.fxaaPass.material.uniforms['resolution'].value.set(1 / width, 1 / height);
    this.composer.addPass(this.fxaaPass);
  }

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  /**
   * Render a frame. Always call this instead of renderer.render() directly.
   * Passing the camera each frame lets the RenderPass track FOV/position
   * changes without GameScene needing to own a camera reference.
   */
  render(camera: THREE.Camera): void {
    if (this.composer && this.renderPass) {
      // Keep the RenderPass in sync with whichever camera Game.ts is using
      this.renderPass.camera = camera;
      this.composer.render();
    } else {
      this.renderer.render(this.scene, camera);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Settings
  // ────────────────────────────────────────────────────────────

  /** Apply client settings changes at runtime */
  applyClientSettings(): void {
    this.renderer.setPixelRatio(clientSettings.getPixelRatio(this.lowQuality));
    this.renderer.shadowMap.enabled = clientSettings.isShadowEnabled();

    // Update shadow map resolution if sun light exists
    if (this.sunLight && clientSettings.isShadowEnabled()) {
      const shadowRes = clientSettings.getShadowMapSize(this.lowQuality);
      this.sunLight.shadow.mapSize.width = shadowRes;
      this.sunLight.shadow.mapSize.height = shadowRes;
      // Force shadow map to regenerate
      if (this.sunLight.shadow.map) {
        this.sunLight.shadow.map.dispose();
        this.sunLight.shadow.map = null as any;
      }
    }

    this.resize();
  }

  // ────────────────────────────────────────────────────────────
  // Theme
  // ────────────────────────────────────────────────────────────

  /** Configure scene lighting and fog for a specific map theme */
  applyTheme(theme: MapTheme): void {
    // Remove previous lights if they exist
    if (this.ambientLight) {
      this.scene.remove(this.ambientLight);
      this.ambientLight.dispose();
      this.ambientLight = null;
    }
    if (this.sunLight) {
      this.scene.remove(this.sunLight);
      this.sunLight.dispose();
      this.sunLight = null;
    }

    // Sky and fog
    this.scene.background = new THREE.Color(theme.skyColor);
    this.scene.fog = new THREE.Fog(theme.fogColor, theme.fogNear, theme.fogFar);

    // Hemisphere light provides a nice sky/ground ambient gradient
    this.ambientLight = new THREE.HemisphereLight(
      theme.ambientColor,
      theme.groundColor,
      theme.ambientIntensity
    );
    this.scene.add(this.ambientLight);

    // Directional sun light with shadows
    this.sunLight = new THREE.DirectionalLight(theme.sunColor, theme.sunIntensity);
    this.sunLight.position.set(
      theme.sunPosition.x,
      theme.sunPosition.y,
      theme.sunPosition.z
    );
    this.sunLight.castShadow = clientSettings.isShadowEnabled();

    // Shadow map configuration based on client settings
    const shadowRes = clientSettings.getShadowMapSize(this.lowQuality);
    this.sunLight.shadow.mapSize.width = shadowRes;
    this.sunLight.shadow.mapSize.height = shadowRes;
    this.sunLight.shadow.camera.near = 1;
    this.sunLight.shadow.camera.far = 200;
    this.sunLight.shadow.camera.left = -80;
    this.sunLight.shadow.camera.right = 80;
    this.sunLight.shadow.camera.top = 80;
    this.sunLight.shadow.camera.bottom = -80;
    this.sunLight.shadow.bias = -0.002;
    this.sunLight.shadow.normalBias = 0.05;

    this.scene.add(this.sunLight);

    // ── Per-map bloom tuning ──
    // Dark-sky maps (space station, haunted mansion) use stronger bloom to
    // make neon road stripes and emissive elements glow dramatically.
    // Bright outdoor maps use a very subtle bloom so surfaces stay clean.
    if (this.bloomPass) {
      const skyLuminance = this.getSkyLuminance(theme.skyColor);
      const isDarkMap = skyLuminance < 0.08; // space_station: 0x0a0a2e, haunted: 0x1a0a2e
      this.bloomPass.strength = isDarkMap ? 0.6 : 0.3;
      this.bloomPass.threshold = isDarkMap ? 0.7 : 0.85;
      this.bloomPass.radius = isDarkMap ? 0.5 : 0.4;
    }
  }

  /**
   * Compute approximate relative luminance of a packed hex color (0..1).
   * Used to distinguish dark (space/haunted) maps from bright outdoor ones.
   */
  private getSkyLuminance(hexColor: number): number {
    const r = ((hexColor >> 16) & 0xff) / 255;
    const g = ((hexColor >> 8) & 0xff) / 255;
    const b = (hexColor & 0xff) / 255;
    // BT.601 luminance coefficients
    return 0.299 * r + 0.587 * g + 0.114 * b;
  }

  // ────────────────────────────────────────────────────────────
  // Scene management
  // ────────────────────────────────────────────────────────────

  /** Remove all objects except lights for a new race */
  clearScene(): void {
    // Only remove top-level children (traverse collects children of children too)
    for (let i = this.scene.children.length - 1; i >= 0; i--) {
      const child = this.scene.children[i];
      if (child !== this.ambientLight && child !== this.sunLight) {
        this.disposeObject(child);
        this.scene.remove(child);
      }
    }
  }

  // ────────────────────────────────────────────────────────────
  // Resize
  // ────────────────────────────────────────────────────────────

  resize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(clientSettings.getPixelRatio(this.lowQuality));

    // Composer and FXAA must know the new pixel dimensions
    if (this.composer) {
      this.composer.setSize(width, height);
    }
    if (this.fxaaPass) {
      this.fxaaPass.material.uniforms['resolution'].value.set(1 / width, 1 / height);
    }
    if (this.bloomPass) {
      this.bloomPass.setSize(width, height);
    }
  }

  /** Get the current aspect ratio */
  getAspect(): number {
    return this.container.clientWidth / this.container.clientHeight;
  }

  // ────────────────────────────────────────────────────────────
  // Disposal
  // ────────────────────────────────────────────────────────────

  dispose(): void {
    window.removeEventListener('resize', this.resize.bind(this));
    this.clearScene();
    if (this.ambientLight) {
      this.scene.remove(this.ambientLight);
      this.ambientLight.dispose();
    }
    if (this.sunLight) {
      this.scene.remove(this.sunLight);
      this.sunLight.dispose();
    }
    if (this.envTexture) {
      this.envTexture.dispose();
      this.envTexture = null;
    }
    if (this.composer) {
      this.composer.dispose();
      this.composer = null;
    }
    this.renderer.dispose();
    this.container.removeChild(this.renderer.domElement);
  }

  private disposeObject(obj: THREE.Object3D): void {
    obj.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
      if (child instanceof THREE.Points) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
  }
}
